import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'

interface Member {
  id: string
  user_id: string
  role: string
  display_name: string | null
  joined_at: string
}

interface PC {
  character_id: string
  character_name: string
  owner_user_id?: string | null
}

interface Props {
  campaignId: string
  pcs: PC[]
  onChanged: () => void
}

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-player`

async function callFunction(action: string, params: Record<string, unknown>) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) throw new Error('Not authenticated')

  const res = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action, ...params }),
  })
  const text = await res.text()
  console.log(`[invite-player] ${action} → ${res.status}:`, text)
  let data: any
  try { data = JSON.parse(text) } catch { throw new Error(`Non-JSON response (${res.status}): ${text.slice(0, 200)}`) }
  if (!res.ok) throw new Error(data.error ?? `Request failed (${res.status})`)
  return data
}

export function PlayerManager({ campaignId, pcs, onChanged }: Props) {
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [busy, setBusy] = useState(false)

  const loadMembers = useCallback(async () => {
    try {
      const data = await callFunction('list-members', { campaign_id: campaignId })
      setMembers(data.members ?? [])
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [campaignId])

  useEffect(() => { loadMembers() }, [loadMembers])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setError('')
    setSuccess('')
    setBusy(true)
    try {
      await callFunction('invite', { campaign_id: campaignId, email: email.trim() })
      setSuccess(`${email.trim()} added as player`)
      setEmail('')
      loadMembers()
    } catch (err) {
      console.error('Invite failed:', err)
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async (userId: string, name: string) => {
    if (!confirm(`Remove ${name} from this campaign? Their character will be unassigned.`)) return
    setError('')
    setBusy(true)
    try {
      await callFunction('remove-member', { campaign_id: campaignId, member_user_id: userId })
      loadMembers()
      onChanged()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const handleAssignPC = async (userId: string, characterId: string) => {
    setError('')
    setBusy(true)
    try {
      await callFunction('assign-pc', {
        campaign_id: campaignId,
        member_user_id: userId,
        character_id: characterId,
      })
      onChanged()
      setSuccess('Character assigned')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const players = members.filter(m => m.role === 'player')
  const unassignedPCs = pcs.filter(pc => !pc.owner_user_id)

  return (
    <div style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #ddd5c4', borderRadius: '8px', background: '#fff' }}>
      <h3 style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>Players</h3>

      {error && <p style={{ color: '#e74c3c', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{error}</p>}
      {success && <p style={{ color: '#6b7c3f', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{success}</p>}

      {/* Current players */}
      {loading ? (
        <p style={{ color: '#666', fontSize: '0.85rem' }}>Loading members...</p>
      ) : players.length === 0 ? (
        <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '0.75rem' }}>No players yet. Invite one below.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
              <th style={{ padding: '0.3rem' }}>Player</th>
              <th>Character</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {players.map(p => {
              const assignedPC = pcs.find(pc => pc.owner_user_id === p.user_id)
              return (
                <tr key={p.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '0.3rem' }}>{p.display_name ?? p.user_id.slice(0, 8)}</td>
                  <td>
                    {assignedPC ? (
                      <strong>{assignedPC.character_name}</strong>
                    ) : unassignedPCs.length > 0 ? (
                      <select
                        defaultValue=""
                        onChange={e => {
                          if (e.target.value) handleAssignPC(p.user_id, e.target.value)
                        }}
                        style={{ padding: '0.2rem', fontSize: '0.8rem', border: '1px solid #ddd' }}
                      >
                        <option value="">— Assign PC —</option>
                        {unassignedPCs.map(pc => (
                          <option key={pc.character_id} value={pc.character_id}>
                            {pc.character_name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ color: '#999' }}>No unassigned PCs</span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => handleRemove(p.user_id, p.display_name ?? 'this player')}
                      disabled={busy}
                      style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', color: '#e74c3c', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '3px', background: 'transparent', cursor: 'pointer' }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {/* Invite form */}
      <form onSubmit={handleInvite} style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Player's email address"
          required
          style={{ flex: 1, padding: '0.4rem 0.6rem', border: '1px solid #ddd5c4', borderRadius: '4px', fontSize: '0.85rem' }}
        />
        <button
          type="submit"
          disabled={busy}
          style={{ padding: '0.4rem 1rem', backgroundColor: '#8b4513', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
        >
          {busy ? 'Adding...' : 'Invite Player'}
        </button>
      </form>
      <p style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.3rem' }}>
        Player must have signed in at least once before they can be invited.
      </p>
    </div>
  )
}
